import { UnbrandedLoadingScreen } from '#helper-components';
import { logError, logMessage } from '#utils';
import { isStaleDeployError, reloadForStaleDeploy } from '../../utils/staleDeploy';
import { Stack, Typography } from '@mui/material';
import { useTranslation } from '@modernloop/i18n';
import React, { FC } from 'react';

// eslint-disable-next-line react-refresh/only-export-components
const SomethingWentWrongMessage: FC = () => {
  const { t } = useTranslation();
  return (
    // The fallback uses direct styles because it must fill the viewport without a parent layout.
    // eslint-disable-next-line no-restricted-syntax
    <Stack direction="column" alignItems="center" justifyContent="center" style={{ minHeight: '100vh' }}>
      <Typography gutterBottom align="center" variant="h4">
        <span aria-label="sad" role="img">
          😢
        </span>
      </Typography>
      <Typography gutterBottom align="center" variant="h4">
        {t('common:shared.error.somethingWentWrong')}
      </Typography>
      <Typography gutterBottom align="center" variant="h5">
        {t('common:shared.error.notifiedMessage')}
      </Typography>
    </Stack>
  );
};

interface Props {
  scope?: string;
  fallback?: React.ReactNode;
  getContext?: () => Record<string, string>;
  children?: React.ReactNode;
}
interface State {
  hasError: boolean;
  showError: boolean;
}

// eslint-disable-next-line modernloop/validate-component-definition.cjs
export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, showError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, showError: !isStaleDeployError(error) };
  }

  componentDidCatch(error: Error): void {
    if (isStaleDeployError(error)) {
      if (!reloadForStaleDeploy(error)) {
        // Reloading did not fix the stale assets (loop guard tripped); show
        // the error UI instead of reloading forever.
        this.setState({ showError: true });
      }
    } else {
      const context = this.props.getContext?.();
      logError(error, { scope: this.props.scope, ...context });
      logMessage('"Something went wrong" toast displayed', 'fatal', { error: error.message });
    }
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      if (this.state.showError) {
        if (this.props.fallback) {
          return this.props.fallback;
        }
        return <SomethingWentWrongMessage />;
      } else {
        return <UnbrandedLoadingScreen />;
      }
    }

    return this.props.children;
  }
}
